import { BasePlugin, Meta, Body } from '@uppy/core'
import { storage } from '../firebase'
import { ref, uploadBytesResumable } from 'firebase/storage'
import { v4 as uuidv4 } from 'uuid'
import { filterNonFailedFiles, filterFilesToEmitUploadStarted } from '@uppy/utils/lib/fileFilters'
import { customFetch, refreshAuthTokens } from './fetch'

export default class FirebaseStorageUpload<M extends Meta, B extends Body> extends BasePlugin<any, M, B> {
  constructor(uppy, opts) {
    super(uppy, opts)
    this.id = opts?.id || 'FirebaseStorageUpload'
    this.type = 'uploader'
    this.handleUpload = this.handleUpload.bind(this)
  }

  async handleUpload(fileIDs: string[]) {
    const files = this.uppy.getFilesByIds(fileIDs)
    const filesFiltered = filterNonFailedFiles(files)
    const filesToEmit = filterFilesToEmitUploadStarted(filesFiltered)
    this.uppy.emit('upload-start', filesToEmit)

    // Refresh tokens immediately before starting upload to ensure we have a fresh token
    await refreshAuthTokens()

    // Start token refresh interval for long uploads
    const tokenRefreshInterval = setInterval(refreshAuthTokens, 40 * 60 * 1000) // Refresh every 40 minutes

    const promises = files
      .filter((file) => !file.isRemote)
      .map((file) => {
        return new Promise((resolve) => {
          // Create a URL for the file
          const audioUrl = URL.createObjectURL(file.data)

          // Create a temporary audio element
          const audioElement = document.createElement('audio')
          audioElement.src = audioUrl

          // Extract duration
          audioElement.addEventListener('loadedmetadata', () => {
            const duration = audioElement.duration
            URL.revokeObjectURL(audioUrl)
            resolve(duration) // Resolve the duration for further use
          })
        }).then((duration) => {
          const storedFileName = uuidv4() + '.' + file.extension
          console.log('Stored file name:', storedFileName)
          const storageRef = ref(storage, `meets/${storedFileName}`)
          const uploadTask = uploadBytesResumable(storageRef, file.data)

          return new Promise((resolve, reject) => {
            uploadTask.on(
              'state_changed',
              (snapshot) => {
                // Observe state change events such as progress
                // const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
                // Update Uppy's file progress for the current file
                this.uppy.emit('upload-progress', file, {
                  uploadStarted: null,
                  bytesUploaded: snapshot.bytesTransferred,
                  bytesTotal: snapshot.totalBytes,
                })
                // console.log('Upload is ' + progress + '% done')
              },
              (error) => {
                // Handle unsuccessful uploads
                this.uppy.emit('upload-error', file, error)
                console.error(error)
                reject(error)
              },
              async () => {
                // Handle successful uploads on complete
                try {
                  const response = await customFetch(`/getMeetUrlByStoredName/${storedFileName}`, 'GET', null, {
                    skipCache: true,
                  })
                  if (response.error) {
                    console.error('Error fetching meet URL:', response.error)
                    this.uppy.emit('upload-error', file, new Error(response.error.message || 'Failed to get file URL'))
                    reject(response.error)
                    return
                  }
                  console.log('File available at', response)
                  this.uppy.emit('upload-success', file, response.url)
                  // Update Uppy's file state with the download URL
                  this.uppy.setFileState(file.id, {
                    uploadURL: JSON.stringify({ url: response.url, storedFileName, duration }),
                  })
                  resolve(file)
                } catch (error) {
                  console.error('Error in upload completion:', error)
                  this.uppy.emit('upload-error', file, error)
                  reject(error)
                }
              }
            )
          })
        })
      })

    try {
      const result = await Promise.all(promises)
      // Clear token refresh interval after all uploads complete
      clearInterval(tokenRefreshInterval)
      return result
    } catch (error) {
      // Clear token refresh interval if there's an error
      clearInterval(tokenRefreshInterval)
      throw error
    }
  }

  install() {
    this.uppy.addUploader(this.handleUpload)
  }

  uninstall() {
    this.uppy.removeUploader(this.handleUpload)
  }
}
